import React, {
    ChangeEvent,
    FocusEvent,
    MouseEvent,
    ReactElement,
    ReactFragment,
} from "react";
import classnames from "classnames";

import { IValidation } from "@formant/core/model/IValidation";

import { concatAriaAttribute } from "../../utils/concatAriaAttribute";
import { recursiveRewriteNode } from "../../utils/recursiveRewriteNode";
import { useUniqueId } from "../../utils/useUniqueId";
import styles from "./Field.module.scss";
import { Icon, IconName } from "./Icon";

export interface IFieldProps {
    children: ReactElement | ReactFragment;
    className?: string;
    collapsed?: boolean;
    errorMessage?: string | IValidation;
    filledOut?: boolean;
    icon?: IconName;
    invalid?: boolean;
    label?: string;
    readOnly?: boolean;
    small?: boolean;
}

export type InputElement =
    | HTMLInputElement
    | HTMLSelectElement
    | HTMLTextAreaElement;

interface IInputElementFieldProps<T extends InputElement | never> extends Omit<
    IFieldProps,
    "children" | "filledOut"
> {
    autoFocus?: boolean;
    disabled?: boolean;
    id?: string;
    name?: string;
    onBlur?: T extends never ? never : (event: FocusEvent<T>) => void;
    onChange?: T extends never ? never : (event: ChangeEvent<T>) => void;
    onClick?: T extends never ? never : (event: MouseEvent<T>) => void;
    onFocus?: T extends never ? never : (event: FocusEvent<T>) => void;
    onKeyDown?: T extends never ? never : React.KeyboardEventHandler<T>;
    value?: string;
}

export type IExoticTypeFieldProps = IInputElementFieldProps<never>;
export type ISelectTypeFieldProps = IInputElementFieldProps<HTMLSelectElement>;
export interface ITextTypeFieldProps<
    T extends HTMLInputElement | HTMLTextAreaElement,
> extends IInputElementFieldProps<T> {
    autoComplete?: string;
    placeholder?: string;
    spellCheck?: boolean;
}

const isFormInputElement = (el: ReactElement) =>
    typeof el.type === "string" &&
    ["input", "select", "textarea"].includes(el.type);

export const Field = (props: IFieldProps) => {
    const {
        children,
        className,
        collapsed,
        errorMessage,
        filledOut,
        icon,
        invalid,
        label,
        readOnly,
        small,
    } = props;

    const labelId = useUniqueId();
    const errorMessageId = useUniqueId();

    const inputNode = recursiveRewriteNode(children, el =>
        isFormInputElement(el)
            ? React.cloneElement(el, {
                  ...concatAriaAttribute(
                      el.props,
                      "aria-labelledby",
                      label ? labelId : null,
                  ),
                  ...concatAriaAttribute(
                      el.props,
                      "aria-describedby",
                      errorMessage ? errorMessageId : null,
                  ),
              })
            : el,
    );

    return (
        <>
            <span
                className={classnames(
                    styles.field,
                    className,
                    collapsed && styles.collapsed,
                    !!small && styles.small,
                    invalid && styles.invalid,
                    readOnly && styles.readonly,
                    !!icon && styles["has-icon"],
                    filledOut && styles["filled-out"],
                )}
            >
                {icon && <Icon name={icon} />}
                {inputNode}
                {label && <label id={labelId}>{label}</label>}
            </span>
            {errorMessage && (
                <div
                    className={classnames(
                        styles["error-message"],
                        !!small && styles["small-error-message"],
                    )}
                    id={errorMessageId}
                    role="alert"
                >
                    {typeof errorMessage === "string"
                        ? errorMessage
                        : Object.entries(errorMessage)
                              .map(([_, errors]) => errors.join(". "))
                              .join(". ") + "."}
                </div>
            )}
        </>
    );
};
